import addTargetEventListener from './events/addTargetEventListener';
import scrollTo from './animation/scrollTo';
import 'element-closest-polyfill';
import addStartupScript from './events/addStartupScript';
import measureText from './string/measureText';
import getCookieByName from './storage/getCookieByName';
import getCountry from './storage/getCountry';
import getLang from './storage/getLang';
import getPreferredLang from './storage/getPreferredLang';

const COUNTRY_PICKER_SELECT = '.mc-country-picker select';
const LOCALE_COOKIE_NAME = 'preferred_locale';
const PREFERRED_LANG_BUTTON = '#preferred-lang-button';
const MODAL_ID = 'languageModal';

function updateDisclaimerLinkText(el) {
  if(el.closest('.mc-footer').classList.contains('js-show-disclaimer')) {
    el.innerHTML = el.innerHTML.replace(/\b(hide|show)\b/i, 'Hide');
  } else {
    el.innerHTML = el.innerHTML.replace(/\b(hide|show)\b/i, 'Show');
  }
}

function toggleDisclaimer(event) {
  event.preventDefault();
  const footer = event.target.closest('.mc-footer');
  if (footer) {
    footer.classList.toggle('js-show-disclaimer');
  }
  updateDisclaimerLinkText(event.target);

  const body = document.body,
    html = document.documentElement,
    height = Math.max(
      body.scrollHeight, body.offsetHeight,
      html.clientHeight, html.scrollHeight, html.offsetHeight
    );

  scrollTo(Math.max(height - window.innerHeight, 0));
}

/**
 * Saves the given locale to a cookie as the user's current preference.
 * @param {String} locale - The locale value to save
 */
function savePreferredLocale(locale) {
  const date = new Date();
  date.setTime(date.getTime() + (365*24*60*60*1000));
  document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; expires=${date.toUTCString()}; path=/`;
}

/**
 * Gets the current alternate link for the href.
 * @param {String} href - The href value for which the alternate is to be retrieved.
 */
function getCurrentAlternate(href = location.href.replace(/\/$/, '')) {
  return document.querySelector(`link[rel=alternate][href="${href}"]`);
}

/**
 * Gets the alternate link for the locale.
 * @param {String} locale - The lang value for which the alternate is to be retrieved.
 */
function getAlternate(locale) {
  const meta = document.querySelector(`link[rel=alternate][hreflang="${locale}"]`);

  if(locale === 'en-US') {
    return meta || document.querySelector('link[rel=alternate][hreflang="x-default"]');
  } else {
    return meta;
  }
}

/**
 * Checks if we have an alternate link defined for the given locale, and if so, redirects
 * @param {String} locale - The hreflang to check
 */
function redirectToAlternate(locale) {
  const alternate = getAlternate(locale);
  if(alternate) { location.href = alternate.getAttribute('href'); }
}

/**
 * Resizes the given picker control to exactly fit its text
 * @param {HTMLSelectElement} picker - The picker control
 */
function resizePicker(picker) {
  if(picker) {
    const text = (picker.selectedOptions[0] && picker.selectedOptions[0].text) || '';
    const styles = getComputedStyle(picker);
    const measurement = measureText(text, styles);
    picker.style.width = `${measurement.width}px`;
  }
}

/**
 * Handles the event of changing the current locale, by resizing the picker,
 * saving the value as a cookie, and optionally redirecting the user if an alternate
 * path exists for the given locale.
 */
function handleChangeCountryPicker() {
  const picker = document.querySelector(COUNTRY_PICKER_SELECT);
  resizePicker(picker);
  savePreferredLocale(picker.value);
  redirectToAlternate(picker.value);
}

/**
 * Sets the preferred_locale cookie to the current language.
 */
function setPreferredLocaleAsCurrent() {
  const currentAlternate = getCurrentAlternate();

  if (currentAlternate) { savePreferredLocale(currentAlternate.getAttribute('hreflang')); }
}

/**
 * Adds the text inside a modal for the user.
 * @param {String} country - The country from which the user is trying to access the app.
 */
function updateModalText(country) {
  const preferredLangCountry = document.getElementById('preferred-lang-country');
  preferredLangCountry.innerHTML = country || 'United States';
}

/**
 * Updates the button text with the peferred language and country.
 * @param {String} dataKey  - Combination of langCode and countryCode (eg: en-US)
 * @param {String} lang     - Language of the user who is accessing the app (eg. English)
 * @param {String} country  - Country from which the user is accessing the app (eg. USA)
 */
function updateButtonAttributes(dataKey, lang, country) {
  const preferredLangButton = document.getElementById('preferred-lang-button');
  preferredLangButton.textContent = lang && country ? `${country} - ${lang}` :
    'United States - English';
  preferredLangButton.setAttribute('data-key', dataKey);
}

/**
 * Updates the attributes inside a modal.
 * @param {String} langCode     - Language code of the user who is accessing the app (eg. en)
 * @param {String} countryCode  - Country code from which the user is accessing the app (eg. US)
 */
function updateModalAttributes(langCode, countryCode) {
  const country = getCountry()[countryCode];
  updateButtonAttributes(`${langCode}-${countryCode}`, getLang()[langCode], country);
  updateModalText(country);
}

/**
 * Displays the modal for the user to select their preferrend language.
 * @param {String} langCode     - Language code of the user who is accessing the app (eg. en)
 * @param {String} countryCode  - Country code from which the user is accessing the app (eg. US)
 */
function showPreferredLangModal(langCode, countryCode) {
  const modal = '<div class="modal modal--open" id="' + MODAL_ID + '" style="text-align: center; display: block; z-index: 100">'
    + '<div class="modal__content"><span class="modal__content-close js-modal-close">&times;</span>'
    + '<p>It looks like you might be in <span id="preferred-lang-country"></span>. Would you like to view locally relevant content?</p>'
    + '<div><button type="button" id="preferred-lang-button" class="button button--primary js-modal-close"> </button></div>'
    + '<div><a href="javascript:void(0)" class="mc-preferred-language-stay-here js-modal-close">No, stay here</a></div></div></div>';

  var div = document.createElement('div');
  document.body.appendChild(div);
  div.outerHTML = modal;

  updateModalAttributes(langCode, countryCode);
}

function closeLanguageModal(event) {
  const modal = event.target;
  if (modal.id === MODAL_ID) {
    modal.parentNode.removeChild(modal);
    if (document.cookie.indexOf(LOCALE_COOKIE_NAME) === -1) {
      setPreferredLocaleAsCurrent();
    }
  }
}

/**
 * To open a modal for the user to select their preferrend language if their location
 * did not match with the alternate link of their preferred language.
 */
async function choosePreferredLang() {
  let preferredLang = getPreferredLang();
  const preferredLangArr = preferredLang && preferredLang.split('-');
  const pathFromUrl = (url) => url.replace(/\/$/, '').replace(/^https?:\/\/[^/]+/, '').replace(/\?.+/, '') || '/';
  const alternates = document.querySelectorAll('link[rel=alternate]');

  if (preferredLangArr && preferredLangArr.length >= 2 && alternates.length) {
    const countryCode = await fetch('/router_loc').then((r) => r.json()).then(data => data.country_code);
    const alternate = countryCode && getAlternate(`${preferredLangArr[0]}-${countryCode}`);

    if (alternate && pathFromUrl(alternate.href) !== pathFromUrl(location.href)) {
      showPreferredLangModal(preferredLangArr[0], countryCode);
    }
  }
}

/**
 * Click handler when a user clicks on the preferred language button or stay here link inside a
 * modal. This will redirect to the correct alternate href (if a preferred language is chosen)
 * and sets the preferred locale cookie.
 */
function handlePreferredLangButtonClick(event) {
  const meta = getAlternate(event.target.dataset.key);
  savePreferredLocale(meta.getAttribute('hreflang'));
  redirectToAlternate(meta.getAttribute('hreflang'));
}

/**
 * Sets up the country picker on startup by setting its current value
 * from a cookie and resizing it appropriately.
 */
export async function setupCountryPicker() {
  const picker = document.querySelector(COUNTRY_PICKER_SELECT);
  const locale = getCookieByName(LOCALE_COOKIE_NAME);
  if(locale && picker) { picker.value = locale; }
  picker && resizePicker(picker);

  if (!locale && picker) { await choosePreferredLang(); }
}

addTargetEventListener('click', '.mc-nav-item--disclaimer', toggleDisclaimer);
addTargetEventListener('click', PREFERRED_LANG_BUTTON, handlePreferredLangButtonClick);
addTargetEventListener('closeModal', `#${MODAL_ID}`, closeLanguageModal);
addTargetEventListener('change', COUNTRY_PICKER_SELECT, handleChangeCountryPicker);
addStartupScript(setupCountryPicker);
